import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { type SWRConfiguration } from 'swr';
import type { TFunction } from 'i18next';
import { ForbiddenError, NotFoundError, isApiError } from '@shared/api/errors/errors';
import {
  COOLING_MAX_MINUTES,
  getCoolingDurationParts,
  minutesInputToSeconds,
  parseCoolingSettings,
} from '@entities/withdrawal';
import { AdminCommandDialog } from '@features/withdrawal';
import { useApiServices } from '@shared/lib/use-api-services';
import { getApiErrorMessage } from '@shared/lib/get-api-error-message';
import type {
  UpdateWithdrawalPolicySettingsPayload,
  WithdrawalPolicySettingsDto,
} from '@shared/api/contracts';
import { Button, Input, Label, Skeleton, useToast } from '@shared/ui';

export interface CoolingServices {
  getPolicySettings: () => Promise<WithdrawalPolicySettingsDto>;
  updatePolicySettings: (payload: UpdateWithdrawalPolicySettingsPayload) => Promise<WithdrawalPolicySettingsDto>;
}

const SWR_KEY = 'withdrawal-policy-settings';

const swrConfig: SWRConfiguration = {
  revalidateOnFocus: false,
  shouldRetryOnError: false,
  errorRetryCount: 0,
};

const formatCoolingDuration = (seconds: number, t: TFunction): string => {
  const { hours, minutes, seconds: rest } = getCoolingDurationParts(seconds);
  const parts: string[] = [];
  if (hours > 0) parts.push(t('withdrawalControl.cooling.unit.hours', { value: hours }));
  if (minutes > 0) parts.push(t('withdrawalControl.cooling.unit.minutes', { value: minutes }));
  if (rest > 0) parts.push(t('withdrawalControl.cooling.unit.seconds', { value: rest }));
  return parts.join(' ');
};

export function CoolingSection({ services }: { services?: CoolingServices }) {
  const { t } = useTranslation('cabinet');
  const { toast } = useToast();
  const api = useApiServices();
  const policy = services ?? api.withdrawals;
  const settings = useSWR(SWR_KEY, () => policy.getPolicySettings(), swrConfig);
  const [minutes, setMinutes] = useState('');
  const [inputError, setInputError] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  // PATCH прошёл, а перечитать настройки не удалось: старое значение показывать нельзя.
  const [refreshFailed, setRefreshFailed] = useState(false);

  const { data, error, isValidating, mutate } = settings;
  const parsed = parseCoolingSettings(data);

  // Порядок важен: ошибка не маскируется старым значением, а повторный запрос показывает загрузку.
  let view: 'loading' | 'error' | 'invalid' | 'ready';
  if (isValidating && (error || refreshFailed || data === undefined)) view = 'loading';
  else if (error || refreshFailed) view = 'error';
  else if (data === undefined) view = 'loading';
  else if (!parsed) view = 'invalid';
  else view = 'ready';

  const retry = () => {
    setRefreshFailed(false);
    void mutate().catch(() => undefined);
  };

  const openSave = () => {
    if (view !== 'ready') return;
    if (minutesInputToSeconds(minutes) == null) {
      setInputError(true);
      return;
    }
    setInputError(false);
    setCommandOpen(true);
  };

  const renderCurrent = () => {
    if (view === 'loading') {
      return <Skeleton className="h-4 w-24" aria-label={t('withdrawalControl.cooling.loading')} />;
    }
    if (view === 'ready' && parsed) {
      return (
        <span className="font-medium" data-testid="cooling-current">
          {parsed.coolingPeriodSeconds === 0
            ? t('withdrawalControl.cooling.none')
            : formatCoolingDuration(parsed.coolingPeriodSeconds, t)}
        </span>
      );
    }
    return null;
  };

  const errorKey = error instanceof ForbiddenError
    ? 'withdrawalControl.cooling.loadForbidden'
    : error instanceof NotFoundError
      ? 'withdrawalControl.cooling.loadNotFound'
      : 'withdrawalControl.cooling.loadError';

  return (
    <section className="flex h-full flex-col space-y-3">
      <h2 className="text-lg font-semibold">{t('withdrawalControl.cooling.title')}</h2>
      <div className="flex items-center gap-2 text-sm text-foreground">
        {view === 'loading' || view === 'ready' ? (
          <>
            <span>{t('withdrawalControl.cooling.currentLabel')}</span>
            {renderCurrent()}
          </>
        ) : (
          <div
            role="alert"
            className="flex w-full flex-col gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 sm:flex-row sm:items-center sm:justify-between"
          >
            <p className="min-w-0 text-sm text-foreground">
              {view === 'invalid' ? t('withdrawalControl.cooling.invalidResponse') : t(errorKey)}
            </p>
            <Button type="button" size="sm" variant="outline" className="w-full shrink-0 sm:w-auto" onClick={retry}>
              {t('withdrawalControl.cooling.retry')}
            </Button>
          </div>
        )}
      </div>
      <p className="text-sm text-foreground">{t('withdrawalControl.cooling.hint')}</p>
      <div className="mt-auto flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-end">
        <div className="w-full space-y-1.5 sm:w-40">
          <Label htmlFor="cooling-minutes" className="text-xs font-medium text-muted-foreground">
            {t('withdrawalControl.fields.minutes')}
          </Label>
          <Input
            id="cooling-minutes"
            inputMode="numeric"
            value={minutes}
            disabled={view !== 'ready'}
            placeholder={`0–${COOLING_MAX_MINUTES}`}
            aria-invalid={inputError}
            onChange={(event) => {
              setMinutes(event.target.value.replace(/\D/g, ''));
              setInputError(false);
            }}
          />
        </div>
        <Button type="button" className="w-full shrink-0 sm:w-auto" disabled={view !== 'ready'} onClick={openSave}>
          {t('withdrawalControl.cooling.save')}
        </Button>
      </div>
      {inputError ? <p className="text-sm text-destructive">{t('withdrawalControl.cooling.invalid')}</p> : null}
      {view !== 'ready' && view !== 'loading' ? (
        <p className="text-xs text-muted-foreground">{t('withdrawalControl.cooling.saveBlocked')}</p>
      ) : null}
      <AdminCommandDialog
        open={commandOpen}
        title={t('withdrawalControl.cooling.save')}
        description={t('withdrawalControl.cooling.hint')}
        onOpenChange={setCommandOpen}
        onConfirm={async (command) => {
          const seconds = minutesInputToSeconds(minutes);
          if (seconds == null || !parsed) {
            toast(t('withdrawalControl.cooling.invalid'), 'error');
            return;
          }
          try {
            await policy.updatePolicySettings({
              version: parsed.version,
              coolingPeriodSeconds: seconds,
              secondFactor: command.secondFactor,
            });
          } catch (saveError) {
            toast(getApiErrorMessage(saveError, t, 'withdrawalControl.cooling.error'), 'error');
            // Конфликт версий: подтягиваем актуальное значение, чтобы админ решал заново.
            if (isApiError(saveError) && saveError.status === 409) retry();
            return;
          }
          setCommandOpen(false);
          setMinutes('');
          try {
            await mutate(() => policy.getPolicySettings(), { revalidate: false, throwOnError: true });
            setRefreshFailed(false);
            toast(t('withdrawalControl.cooling.saved'), 'success');
          } catch {
            setRefreshFailed(true);
            toast(t('withdrawalControl.cooling.savedRefreshFailed'), 'error');
          }
        }}
      />
    </section>
  );
}
